import type { RentalOrder, RentalItem } from '@forq/rental-data';
import { intervalDates } from '@forq/rental-data';
import type { TenantCompanyProfile } from '@forq/platform-data';

/**
 * Miet-Belege als druckfertiges HTML im FTW-Layout -- Mietangebot, Auftragsbestätigung/Mietvertrag,
 * Lieferschein, Rücknahmeschein. Nachgebaut nach der echten Mietauftragsbestätigung des Kunden:
 * Logo links + Leistungsblock rechts, Meta-Feld (Nummer/Datum/Bearbeiter/Status), Abschnitte
 * „Miet- und Lieferdaten" und „Mietobjekt" (technische Daten + Foto), Kalkulation mit
 * Berechnungsweg, Bedingungen und Unterschriftsfeldern am Ende, vierspaltige Fußzeile.
 *
 * DATEN-getrieben (kein hinterlegtes PDF): Firmenkopf aus dem Firmenprofil, Maschinendaten aus dem
 * Maschinenstamm (von RentalPage aufgelöst und fertig übergeben -- dieser Renderer bleibt entkoppelt),
 * Beträge aus denselben Sätzen wie die Abrechnung. Gedruckt/als PDF gespeichert wird über den Browser.
 */

export type RentalDocumentKind = 'offer' | 'confirmation' | 'delivery' | 'return';

export const rentalDocumentLabels: Readonly<Record<RentalDocumentKind, string>> = {
  offer: 'Mietangebot',
  confirmation: 'Auftragsbestätigung / Mietvertrag',
  delivery: 'Lieferschein',
  return: 'Rücknahmeschein',
};

/** Kopftitel wie in der Vorlage (Großschreibung). */
const documentHeading: Readonly<Record<RentalDocumentKind, string>> = {
  offer: 'MIETANGEBOT',
  confirmation: 'MIETAUFTRAGSBESTÄTIGUNG',
  delivery: 'LIEFERSCHEIN',
  return: 'RÜCKNAHMESCHEIN',
};

/** Welche Belege zu einem Auftrag im jeweiligen Status sinnvoll sind. */
export function availableRentalDocuments(order: RentalOrder): readonly RentalDocumentKind[] {
  const docs: RentalDocumentKind[] = [];
  if (order.status === 'request' || order.status === 'reserved') docs.push('offer');
  if (order.status !== 'request' && order.status !== 'cancelled') docs.push('confirmation');
  if (['reserved', 'active', 'return_scheduled', 'completed'].includes(order.status)) docs.push('delivery');
  if (['return_scheduled', 'completed'].includes(order.status)) docs.push('return');
  return docs;
}

/** Von RentalPage aus dem Maschinenstamm aufgelöste Angaben zu einer gemieteten Maschine. */
export interface RentalDocMachineInfo {
  readonly number: string;
  readonly manufacturer: string;
  readonly model: string;
  readonly serialNumber: string;
  readonly constructionYear: number | null;
  readonly typeName: string;
  readonly location: string;
  readonly imageUrl: string;
  /** Technische Felder (Antrieb, Tragkraft, …) bereits mit Label + Einheit aufgelöst. */
  readonly specs: readonly { readonly label: string; readonly value: string }[];
}

export interface RentalDocContext {
  readonly order: RentalOrder;
  readonly company: TenantCompanyProfile;
  readonly todayIso: string;
  readonly employeeName: string;
  /** Anschrift des Kunden (aus dem Kundenstamm), Zeile für Zeile -- leer = Rückfall auf den Einsatzort. */
  readonly customerAddressLines: readonly string[];
  /** Maschinenstamm-Angaben je Maschinennummer; fehlt eine, wird nur das Label aus dem Auftrag gezeigt. */
  readonly machines: Readonly<Record<string, RentalDocMachineInfo>>;
}

// --- Formatierung -----------------------------------------------------------

const euro = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' });
function money(value: number): string { return euro.format(value); }
/** TT.MM.JJJJ mit führenden Nullen -- ordentlicher als das lokalisierte „18.7.2026". */
function dateLabel(value: string | null): string {
  if (!value) return '–';
  const [year, month, day] = value.split('-');
  return year && month && day ? `${day}.${month}.${year}` : value;
}
function esc(value: string): string {
  return value.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string));
}
function addDays(iso: string, days: number): string {
  const date = new Date(`${iso}T00:00:00.000Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}
function plural(count: number, one: string, many: string): string {
  return `${count} ${count === 1 ? one : many}`;
}
function rentalDays(fromIso: string, toIso: string): number {
  return Math.max(1, Math.round((Date.parse(toIso) - Date.parse(fromIso)) / 86_400_000) + 1);
}

// --- Beträge & Berechnungsweg ----------------------------------------------

/** Staffel-Aufschlüsselung wie computeItemCharge, aber zusätzlich als lesbarer Berechnungsweg. */
function chargeBreakdown(item: RentalItem, fromIso: string, toIso: string): { parts: string[]; total: number } {
  let remaining = Math.max(rentalDays(fromIso, toIso), item.minimumDays || 1);
  const parts: string[] = [];
  let total = 0;
  if (item.monthlyRate > 0) {
    const months = Math.floor(remaining / 30);
    if (months > 0) { parts.push(`${plural(months, 'Monat', 'Monate')} × ${money(item.monthlyRate)}`); total += months * item.monthlyRate; remaining -= months * 30; }
  }
  if (item.weeklyRate > 0) {
    const weeks = Math.floor(remaining / 7);
    if (weeks > 0) { parts.push(`${plural(weeks, 'Woche', 'Wochen')} × ${money(item.weeklyRate)}`); total += weeks * item.weeklyRate; remaining -= weeks * 7; }
  }
  if (remaining > 0 || parts.length === 0) { parts.push(`${plural(remaining, 'Tag', 'Tage')} × ${money(item.dailyRate)}`); total += remaining * item.dailyRate; }
  return { parts, total: Math.round(total * 100) / 100 };
}

function itemCharge(order: RentalOrder, item: RentalItem): { berechnung: string; total: number } {
  const end = item.actualReturnDate ?? item.endDate;
  if (order.kind === 'interval' && order.intervalWeekday !== null) {
    const occ = intervalDates(item.startDate, end, order.intervalWeekday).length;
    return { berechnung: `${plural(occ, 'Einsatz', 'Einsätze')} × ${money(item.dailyRate)}`, total: Math.round(occ * item.dailyRate * 100) / 100 };
  }
  const { parts, total } = chargeBreakdown(item, item.startDate, end);
  return { berechnung: parts.join(' + '), total };
}

function transportTotal(order: RentalOrder): number {
  if (order.transportCost <= 0) return 0;
  if (order.kind === 'interval' && order.intervalWeekday !== null) {
    return intervalDates(order.startDate, order.endDate, order.intervalWeekday).length * 2 * order.items.length * order.transportCost;
  }
  return order.transportCost;
}

// --- Bausteine --------------------------------------------------------------

/** Zweispaltige Datenzeile im „Miet- und Lieferdaten"-Kasten. */
function dataRow(l1: string, v1: string, l2: string, v2: string): string {
  return `<tr><th>${esc(l1)}</th><td>${v1}</td><th>${esc(l2)}</th><td class="r">${v2}</td></tr>`;
}

function mietLieferdaten(order: RentalOrder): string {
  const delivery = order.transports.find((t) => t.type === 'delivery')?.plannedDate ?? order.startDate;
  const pickup = order.transports.find((t) => t.type === 'pickup')?.plannedDate ?? order.endDate;
  const site = order.siteAddress ? esc(order.siteAddress).replace(/,\s*/g, '<br>') : '–';
  const kontakt = order.contactName ? `${esc(order.contactName)}${order.contactPhone ? ` · ${esc(order.contactPhone)}` : ''}` : '–';
  return `
    <h2 class="sec">Miet- und Lieferdaten</h2>
    <table class="box">
      ${dataRow('Mietbeginn', dateLabel(order.startDate), 'Mietende geplant', dateLabel(order.endDate))}
      ${dataRow('Auslieferung', order.deliveryRequired ? dateLabel(delivery) : 'Selbstabholung', 'Abholung', order.pickupRequired ? dateLabel(pickup) : 'Selbstanlieferung')}
      <tr><th>Lieferadresse</th><td colspan="1">${site}</td><th>Kontakt vor Ort</th><td class="r">${kontakt}</td></tr>
    </table>`;
}

function mietobjekt(order: RentalOrder, machines: RentalDocContext['machines']): string {
  return order.items.map((item) => {
    const m = machines[item.machineNumber];
    const rows: { l: string; v: string }[] = [
      { l: 'Gerät', v: `${esc(item.machineNumber)} – ${esc(item.machineLabel)}` },
    ];
    if (m) {
      if (m.manufacturer || m.model) rows.push({ l: 'Hersteller / Typ', v: esc([m.manufacturer, m.model].filter(Boolean).join(' / ')) });
      if (m.serialNumber) rows.push({ l: 'Fabr.-Nr.', v: esc(m.serialNumber) });
      if (m.typeName) rows.push({ l: 'Maschinenart', v: esc(m.typeName) });
      if (m.constructionYear) rows.push({ l: 'Baujahr', v: String(m.constructionYear) });
      m.specs.forEach((s) => rows.push({ l: s.label, v: esc(s.value) }));
      if (m.location) rows.push({ l: 'Standort', v: esc(m.location) });
    }
    const photo = m?.imageUrl
      ? `<div class="obj-photo"><img src="${esc(m.imageUrl)}" alt="${esc(item.machineLabel)}"></div>`
      : '<div class="obj-photo obj-photo--empty">kein Foto hinterlegt</div>';
    return `
      <table class="box obj">
        <tr>
          <td class="obj-data">
            <table class="obj-specs">${rows.map((r) => `<tr><th>${esc(r.l)}</th><td>${r.v}</td></tr>`).join('')}</table>
          </td>
          <td class="obj-img">${photo}</td>
        </tr>
      </table>`;
  }).join('');
}

function kalkulation(order: RentalOrder): string {
  const rows = order.items.map((item) => {
    const { berechnung, total } = itemCharge(order, item);
    return `<tr><td>${esc(item.machineNumber)} – ${esc(item.machineLabel)}</td><td>${esc(berechnung)}</td><td class="num">${money(total)}</td></tr>`;
  });
  const transport = transportTotal(order);
  if (transport > 0) {
    const label = order.kind === 'interval' ? 'Transport (Zustellung + Abholung je Einsatz)' : 'Transport (An- und Abfahrt)';
    rows.push(`<tr><td>${esc(label)}</td><td></td><td class="num">${money(transport)}</td></tr>`);
  }
  const net = order.items.reduce((s, item) => s + itemCharge(order, item).total, 0) + transport;
  const vat = Math.round(net * 0.19 * 100) / 100;
  return `
    <h2 class="sec">Mietkalkulation (netto)</h2>
    <table class="calc">
      <thead><tr><th>Leistung</th><th>Berechnung</th><th class="num">Betrag netto</th></tr></thead>
      <tbody>${rows.join('')}</tbody>
    </table>
    <table class="sum">
      <tr><td>Voraussichtliche Gesamtsumme netto:</td><td class="num">${money(net)}</td></tr>
      <tr><td>zzgl. gesetzliche MwSt. (19 %):</td><td class="num">${money(vat)}</td></tr>
      <tr class="grand"><td>Voraussichtlicher Gesamtbetrag:</td><td class="num">${money(net + vat)}</td></tr>
    </table>
    ${order.depositAmount > 0 ? `<p class="note">Kaution: <strong>${money(order.depositAmount)}</strong> – wird bei ordnungsgemäßer Rückgabe verrechnet bzw. erstattet und ist nicht Bestandteil der Mietkalkulation.</p>` : ''}`;
}

function conditionRows(order: RentalOrder, kind: 'delivery' | 'return'): string {
  const rows = order.items.map((item) => {
    const meter = kind === 'delivery' ? item.meterOut : item.meterIn;
    const damage = kind === 'return' && item.damageAmount > 0 ? `${esc(item.damageNote || 'Schaden')} (${money(item.damageAmount)})` : '';
    return `<tr>
      <td>${esc(item.machineNumber)} – ${esc(item.machineLabel)}</td>
      <td>${meter ?? '<span class="blank"></span>'}</td>
      <td>${kind === 'return' ? esc(damage) : ''}<span class="blank"></span></td>
    </tr>`;
  }).join('');
  return `
    <h2 class="sec">${kind === 'delivery' ? 'Übergabe' : 'Rücknahme'}</h2>
    <table class="calc">
      <thead><tr><th>Maschine</th><th>${kind === 'delivery' ? 'Betriebsstunden raus' : 'Betriebsstunden rein'}</th><th>${kind === 'return' ? 'Zustand / Schäden' : 'Zustand bei Übergabe'}</th></tr></thead>
      <tbody>${rows}</tbody>
    </table>`;
}

/** Die FTW-Bedingungen; Firmenname und Rückgabeort aus dem Profil eingesetzt. */
function terms(company: TenantCompanyProfile): string {
  const name = company.identity.name || 'des Vermieters';
  const ort = company.identity.address.city ? `am Standort ${esc(company.identity.address.city)}` : 'am Standort des Vermieters';
  const mail = company.contact.email;
  return `
    <h2 class="sec">Liefer- und Rückgabebedingungen</h2>
    <p class="terms">Die Mietgebühren verstehen sich netto, zuzüglich gesetzlicher Mehrwertsteuer. Verbrauchsmaterial, Kraftstoffdifferenzen, Reinigung, Schäden und weitere Zusatzkosten sind nicht Bestandteil der Mietkalkulation.</p>
    <p class="terms">Das Mietobjekt wird gereinigt und – soweit zutreffend – betankt übergeben. Es ist gereinigt und in vergleichbarem Zustand zurückzugeben. Bei verspäteter Rückgabe ohne vorherige Absprache wird die zusätzliche Mietzeit berechnet. Die Rückgabe erfolgt grundsätzlich ${ort}, sofern nichts anderes vereinbart wurde.</p>
    <h2 class="sec">Vereinbarungen zur Vermietung</h2>
    <ul class="terms-list">
      <li>Die Vermietung erfolgt unter Anerkennung der vereinbarten Geschäftsbedingungen. Das Mietobjekt bleibt Eigentum der ${esc(name)}.</li>
      <li>Der Mieter bestätigt die Übernahme des Mietobjekts in ordnungsgemäßem Zustand nach sachgerechter Einweisung. Beanstandungen werden nur berücksichtigt, sofern sie bei der Auslieferung schriftlich erfasst werden.</li>
      <li>Der Mieter verpflichtet sich, Bedienungs-, Betriebs- und Wartungsanleitungen sowie die geltenden Sicherheitsvorschriften vollständig zu beachten.</li>
      <li>Kosten und Reparaturen infolge unsachgemäßer Bedienung, nicht sachgerechter Sicherung oder Beschädigung des Mietobjekts trägt der Mieter.</li>
      <li>Der Mieter ist in die notwendigen Sicherungsmaßnahmen für den Transport einzuweisen. Transporte dürfen ausschließlich mit hierfür geeigneten und zugelassenen Fahrzeugen oder Anhängern durchgeführt werden.</li>
      <li>Bei verspäteter Rückgabe ohne vorherige Absprache wird die Zeitdifferenz in den Mietgebühren berücksichtigt.</li>
    </ul>
    ${mail ? `<p class="terms">Bitte senden Sie diesen Beleg unterzeichnet zurück: per E-Mail an ${esc(mail)}.</p>` : ''}`;
}

function signatures(company: TenantCompanyProfile): string {
  return `
    <div class="signs">
      <div><span class="sig-line"></span>Ort / Datum</div>
      <div><span class="sig-line"></span>Unterschrift Mieter</div>
      <div><span class="sig-line"></span>Auftrag angenommen<br>${esc(company.identity.name || '')}</div>
    </div>`;
}

// --- Zusammenbau je Belegart ------------------------------------------------

function bodyFor(kind: RentalDocumentKind, ctx: RentalDocContext): string {
  const { order, company, todayIso, machines } = ctx;
  if (kind === 'offer' || kind === 'confirmation') {
    const validUntil = addDays(todayIso, company.documentDefaults.quoteValidityDays || 30);
    return `
      ${mietLieferdaten(order)}
      <h2 class="sec">Mietobjekt</h2>
      ${mietobjekt(order, machines)}
      ${kalkulation(order)}
      ${kind === 'offer' ? `<p class="note">Dieses Angebot ist gültig bis <strong>${dateLabel(validUntil)}</strong>.</p>` : ''}
      ${terms(company)}
      ${kind === 'confirmation' ? signatures(company) : ''}`;
  }
  // Lieferschein / Rücknahmeschein
  return `
    ${mietLieferdaten(order)}
    <h2 class="sec">Mietobjekt</h2>
    ${mietobjekt(order, machines)}
    ${conditionRows(order, kind === 'delivery' ? 'delivery' : 'return')}
    <p class="note">${kind === 'delivery'
      ? 'Bitte prüfen Sie den Mietgegenstand bei Übergabe auf Vollständigkeit und Zustand.'
      : 'Betriebsstunden und Zustand bei Rücknahme werden hier festgehalten; festgestellte Schäden werden mit der Schlussabrechnung berechnet.'}</p>
    <div class="signs">
      <div><span class="sig-line"></span>Ort / Datum</div>
      <div><span class="sig-line"></span>${kind === 'delivery' ? 'Übergeben (Fahrer)' : 'Zurückgenommen (Fahrer)'}</div>
      <div><span class="sig-line"></span>${kind === 'delivery' ? 'Erhalten (Kunde)' : 'Übergeben (Kunde)'}</div>
    </div>`;
}

// --- Shell (Kopf, Fußzeile, Druck-CSS) -------------------------------------

function documentShell(kind: RentalDocumentKind, ctx: RentalDocContext): string {
  const { order, company, todayIso, employeeName } = ctx;
  const identity = company.identity;
  const accent = company.letterhead.accentColor || '#e30613';
  const senderLine = company.letterhead.senderLine
    || [identity.name, identity.address.street, `${identity.address.postalCode} ${identity.address.city}`.trim()].filter(Boolean).join(', ');
  const { contact, bank } = company;
  const footerCol = (heading: string, lines: (string | false)[]) =>
    `<div><span class="fh">${esc(heading)}</span>${lines.filter(Boolean).map((l) => esc(l as string)).join('\n')}</div>`;
  const footer = `<div class="footer">
    ${footerCol(identity.name || 'Firma', [identity.address.street, `${identity.address.postalCode} ${identity.address.city}`.trim()])}
    ${footerCol('Kontakt', [contact.phone && `Tel.: ${contact.phone}`, contact.fax && `Fax: ${contact.fax}`, contact.email, contact.website])}
    ${footerCol('Bankverbindung', [bank.bankName, bank.iban && `IBAN: ${bank.iban}`, bank.bic && `BIC: ${bank.bic}`])}
    ${footerCol('Unternehmen', [identity.managingDirectors && `Geschäftsführer: ${identity.managingDirectors}`, identity.registerCourt && identity.register && `${identity.registerCourt} ${identity.register}`, identity.vatId && `USt-IdNr.: ${identity.vatId}`])}
  </div>`;
  // Briefkopf aus drei Bild-Slots (Firmenprofil): links / Mitte / rechts. Fallbacks: links der
  // Firmenname, rechts der Leistungs-Text -- bis eigene Grafiken hochgeladen sind.
  const lh = company.letterhead;
  const slotLeft = lh.logoDataUrl
    ? `<img src="${esc(lh.logoDataUrl)}" alt="${esc(identity.name)}">`
    : `<div class="logo-name">${esc(identity.name || 'FORQ Mietpark')}</div>`;
  const slotCenter = lh.logoCenterDataUrl ? `<img src="${esc(lh.logoCenterDataUrl)}" alt="">` : '';
  const slotRight = lh.logoRightDataUrl
    ? `<img src="${esc(lh.logoRightDataUrl)}" alt="">`
    : `<div class="services">Handel&nbsp;&nbsp;Service<br>Ersatzteile&nbsp;&nbsp;Vermietung<br><span class="red">für Flurförderzeuge</span></div>`;

  // Empfänger-Anschrift: bevorzugt die Kundenadresse aus dem Stamm, sonst der Einsatzort des Auftrags.
  const addressLines = ctx.customerAddressLines.length > 0
    ? ctx.customerAddressLines
    : (order.siteAddress ? order.siteAddress.split(',').map((line) => line.trim()).filter(Boolean) : []);
  const recipient = `
    <div class="sender">${esc(senderLine)}</div>
    <div class="recipient">
      <div class="cust">${esc(order.customerName || 'Kunde')}</div>
      ${addressLines.map((line) => `<div>${esc(line)}</div>`).join('')}
    </div>`;
  const meta = `
    <div class="doc-head">
      <div class="doc-title">${esc(documentHeading[kind])}</div>
      <table class="meta">
        <tr><th>Nummer:</th><td>${esc(order.number)}</td></tr>
        <tr><th>Datum:</th><td>${dateLabel(todayIso)}</td></tr>
        ${employeeName ? `<tr><th>Bearbeiter:</th><td>${esc(employeeName)}</td></tr>` : ''}
        <tr><th>Kundennr.:</th><td>${esc(order.customerNumber || '–')}</td></tr>
      </table>
    </div>`;

  return `<!doctype html>
<html lang="de"><head><meta charset="utf-8"><title>${esc(rentalDocumentLabels[kind])} ${esc(order.number)}</title>
<style>
  :root { --accent: ${esc(accent)}; --label: #0f6b5c; }
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; }
  body { font-family: "Segoe UI", Arial, sans-serif; color: #1a1a1a; font-size: 11.5px; line-height: 1.4; background: #eee; }
  .toolbar { position: sticky; top: 0; z-index: 5; display: flex; justify-content: center; padding: 8px; background: #222; }
  .toolbar button { font: inherit; font-size: 13px; padding: 7px 16px; border: 0; border-radius: 6px; cursor: pointer; background: var(--accent); color: #fff; }
  .sheet { width: 210mm; margin: 12px auto; padding: 14mm 16mm; background: #fff; box-shadow: 0 1px 6px rgba(0,0,0,0.18); position: relative; }
  .hd { display: flex; align-items: flex-start; gap: 8mm; }
  .hd-slot { flex: 1; display: flex; align-items: flex-start; min-width: 0; }
  .hd-left { justify-content: flex-start; }
  .hd-center { justify-content: center; }
  .hd-right { justify-content: flex-end; }
  .hd-slot img { max-height: 64px; max-width: 100%; object-fit: contain; }
  .logo-name { font-size: 22px; font-weight: 800; color: var(--accent); }
  .services { text-align: right; font-size: 13px; font-weight: 700; line-height: 1.25; }
  .services .red { color: var(--accent); }
  .hd-rule { border: 0; border-top: 3px solid #111; margin: 8px 0 0; }
  .band { display: flex; justify-content: space-between; gap: 12mm; margin-top: 12mm; }
  .sender { font-size: 8px; color: #666; text-decoration: underline; margin-bottom: 10px; }
  .recipient { font-size: 13px; line-height: 1.5; }
  .recipient .cust { font-weight: 700; }
  .doc-head { min-width: 78mm; border-left: 3px solid var(--accent); padding-left: 10px; }
  .doc-title { font-size: 17px; font-weight: 800; letter-spacing: 0.01em; margin-bottom: 6px; }
  table.meta { border-collapse: collapse; font-size: 11px; }
  table.meta th { text-align: left; color: #444; font-weight: 500; padding: 1px 14px 1px 0; }
  table.meta td { font-weight: 700; }
  .band-rule { border: 0; border-top: 1px solid #bbb; margin: 6px 0 0; }
  h2.sec { font-size: 13.5px; font-weight: 700; margin: 8mm 0 3mm; }
  table.box { width: 100%; border-collapse: collapse; border: 1px solid #ccc; }
  table.box th { text-align: left; color: var(--label); font-weight: 700; padding: 6px 8px; width: 24%; vertical-align: top; border: 1px solid #e2e2e2; background: #fafafa; }
  table.box td { padding: 6px 8px; vertical-align: top; border: 1px solid #e2e2e2; }
  table.box td.r { text-align: right; }
  table.box.obj { border: 1px solid #ccc; }
  table.box.obj td { border: 0; }
  .obj-data { width: 62%; }
  .obj-img { width: 38%; vertical-align: middle; padding: 8px; }
  table.obj-specs { width: 100%; border-collapse: collapse; }
  table.obj-specs th { width: 42%; color: var(--label); font-weight: 700; text-align: left; padding: 2px 6px; border: 0; background: none; vertical-align: top; }
  table.obj-specs td { padding: 2px 6px; border: 0; }
  .obj-photo img { width: 100%; max-height: 48mm; object-fit: contain; border: 1px solid #e2e2e2; }
  .obj-photo--empty { display: flex; align-items: center; justify-content: center; height: 28mm; color: #aaa; font-size: 10px; border: 1px dashed #ccc; }
  table.calc { width: 100%; border-collapse: collapse; margin-bottom: 4mm; }
  table.calc th { text-align: left; color: #888; font-weight: 500; font-size: 10px; text-transform: uppercase; letter-spacing: 0.03em; padding: 5px 6px; border-bottom: 1px solid #999; }
  table.calc td { padding: 7px 6px; border-bottom: 1px solid #e2e2e2; vertical-align: top; }
  table.calc .num { text-align: right; white-space: nowrap; }
  table.calc .blank { display: inline-block; min-width: 34mm; border-bottom: 1px dotted #bbb; height: 1em; }
  table.sum { margin-left: auto; border-collapse: collapse; font-size: 12px; }
  table.sum td { padding: 4px 6px; }
  table.sum td.num { text-align: right; font-weight: 700; min-width: 34mm; }
  table.sum tr.grand td { border-top: 1.5px solid #333; font-size: 13.5px; }
  .note { font-size: 10.5px; color: #333; margin: 3mm 0; }
  .terms { font-size: 10px; color: #333; margin: 2mm 0; text-align: justify; }
  .terms-list { font-size: 10px; color: #333; margin: 2mm 0; padding-left: 15px; }
  .terms-list li { margin: 1.5mm 0; }
  .signs { display: flex; gap: 12mm; margin-top: 16mm; }
  .signs div { flex: 1; font-size: 10px; color: #444; }
  .signs .sig-line { display: block; border-top: 1px solid #333; margin-bottom: 3px; height: 12mm; }
  .footer { margin-top: 14mm; border-top: 1.5px solid var(--accent); padding-top: 5px; display: flex; gap: 8mm; }
  .footer div { flex: 1; font-size: 8px; color: #555; white-space: pre-line; line-height: 1.4; }
  .footer .fh { color: var(--accent); font-weight: 700; display: block; margin-bottom: 2px; }
  @media print {
    body { background: #fff; }
    .toolbar { display: none; }
    .sheet { margin: 0; box-shadow: none; width: auto; padding: 0; }
    @page { size: A4; margin: 12mm 14mm; }
    /* Fußzeile läuft im normalen Fluss ans Ende (letzte Seite) -- so darf der Beleg BELIEBIG viele
       Seiten haben, ohne dass Inhalt abgeschnitten wird oder eine fixierte Fußzeile ihn überdeckt. */
    .footer { margin-top: 12mm; }
    /* Blöcke möglichst nicht über den Seitenumbruch zerreißen. */
    h2.sec { break-after: avoid; break-inside: avoid; }
    table.box.obj, .signs, table.sum { break-inside: avoid; }
    table.calc tr { break-inside: avoid; }
  }
</style></head>
<body>
  <div class="toolbar"><button type="button" onclick="window.print()">Drucken / als PDF speichern</button></div>
  <div class="sheet">
    <div class="hd">
      <div class="hd-slot hd-left">${slotLeft}</div>
      <div class="hd-slot hd-center">${slotCenter}</div>
      <div class="hd-slot hd-right">${slotRight}</div>
    </div>
    <hr class="hd-rule">
    <div class="band">
      <div>${recipient}</div>
      ${meta}
    </div>
    <hr class="band-rule">
    ${bodyFor(kind, ctx)}
    ${footer}
  </div>
</body></html>`;
}

/** Das vollständige HTML eines Belegs -- zum Anzeigen im iframe (srcDoc) und Drucken. */
export function renderRentalDocumentHtml(kind: RentalDocumentKind, ctx: RentalDocContext): string {
  return documentShell(kind, ctx);
}
